// src/pages/client/ClientAppointments.jsx
import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  FiCalendar,
  FiClock,
  FiUser,
  FiVideo,
  FiPhone,
  FiMapPin,
  FiPlus,
  FiFilter,
  FiEdit,
  FiTrash2,
  FiCheck,
  FiX,
  FiRefreshCw,
} from "react-icons/fi";
import { useClient } from "../context/ClientContext";
import axios from "axios";

function ClientAppointments() {
  const { client } = useClient();
  const [appointments, setAppointments] = useState([]);
  const [filteredAppointments, setFilteredAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);

  const statuses = [
    { value: "all", label: "All Appointments", color: "gray" },
    { value: "requested", label: "Requested", color: "blue" },
    { value: "confirmed", label: "Confirmed", color: "green" },
    { value: "completed", label: "Completed", color: "purple" },
    { value: "cancelled", label: "Cancelled", color: "red" },
    { value: "rescheduled", label: "Rescheduled", color: "orange" },
  ];

  const appointmentTypes = [
    { value: "virtual", label: "Virtual Meeting", icon: FiVideo },
    { value: "onsite", label: "On-site Visit", icon: FiMapPin },
    { value: "phone", label: "Phone Call", icon: FiPhone },
  ];

  const [bookingData, setBookingData] = useState({
    title: "",
    description: "",
    type: "virtual",
    scheduledDate: "",
    duration: 60,
    clientNotes: "",
    location: "",
  });

  useEffect(() => {
    fetchAppointments();
  }, []);

  useEffect(() => {
    filterAppointments();
  }, [appointments, statusFilter]);

  const fetchAppointments = async () => {
    try {
      const token = localStorage.getItem("agritech-client-token");
      const response = await axios.get(
        "https://agri-back-mauve.vercel.app/api/client-portal/appointments",
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        setAppointments(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching appointments:", error);
    } finally {
      setLoading(false);
    }
  };

  const filterAppointments = () => {
    let filtered = appointments;

    if (statusFilter !== "all") {
      filtered = filtered.filter(
        (appointment) => appointment.status === statusFilter
      );
    }

    setFilteredAppointments(filtered);
  };

  const handleBookingChange = (field, value) => {
    setBookingData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleBookAppointment = async () => {
    // Validate form
    if (!bookingData.title || !bookingData.scheduledDate) {
      alert("Please fill in all required fields");
      return;
    }

    try {
      const token = localStorage.getItem("agritech-client-token");
      const response = await axios.post(
        "https://agri-back-mauve.vercel.app/api/client-portal/appointments",
        bookingData,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        setAppointments((prev) => [response.data.data, ...prev]);
        setShowBookingModal(false);
        setBookingData({
          title: "",
          description: "",
          type: "virtual",
          scheduledDate: "",
          duration: 60,
          clientNotes: "",
          location: "",
        });
        alert(
          "Appointment request submitted successfully! Admin will review and confirm."
        );
      }
    } catch (error) {
      console.error("Error booking appointment:", error);
      alert(
        "Error booking appointment: " +
          (error.response?.data?.message || error.message)
      );
    }
  };

  const handleCancelAppointment = async (appointmentId) => {
    if (!window.confirm("Are you sure you want to cancel this appointment?")) {
      return;
    }

    try {
      const token = localStorage.getItem("agritech-client-token");
      const response = await axios.put(
        `https://agri-back-mauve.vercel.app/api/client-portal/appointments/${appointmentId}/cancel`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        setAppointments((prev) =>
          prev.map((appt) =>
            appt._id === appointmentId ? response.data.data : appt
          )
        );
        alert("Appointment cancelled successfully");
      }
    } catch (error) {
      console.error("Error cancelling appointment:", error);
      alert("Error cancelling appointment: " + error.response?.data?.message);
    }
  };

  const getStatusColor = (status) => {
    const statusConfig = statuses.find((s) => s.value === status);
    return statusConfig ? statusConfig.color : "gray";
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "requested":
        return <FiClock className="w-4 h-4" />;
      case "confirmed":
        return <FiCheck className="w-4 h-4" />;
      case "cancelled":
        return <FiX className="w-4 h-4" />;
      case "rescheduled":
        return <FiRefreshCw className="w-4 h-4" />;
      default:
        return <FiCalendar className="w-4 h-4" />;
    }
  };

  const getTypeIcon = (type) => {
    const typeConfig = appointmentTypes.find((t) => t.value === type);
    const Icon = typeConfig ? typeConfig.icon : FiCalendar;
    return <Icon className="w-4 h-4" />;
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString("en-US", {
        weekday: "short",
        year: "numeric",
        month: "short",
        day: "numeric",
      }),
      time: date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
      }),
      full: date.toLocaleString("en-US", {
        dateStyle: "full",
        timeStyle: "short",
      }),
    };
  };

  const isUpcoming = (dateString) => {
    return new Date(dateString) > new Date();
  };

  const canCancel = (appointment) => {
    return (
      ["requested", "confirmed", "rescheduled"].includes(appointment.status) &&
      isUpcoming(appointment.scheduledDate)
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-green-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your appointments...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold text-gray-900">Appointments</h1>
            </div>
            <button
              onClick={() => setShowBookingModal(true)}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2"
            >
              <FiPlus size={16} />
              <span>Request Appointment</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex items-center gap-4">
            <FiFilter className="text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              {statuses.map((status) => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
            <div className="text-sm text-gray-500 ml-auto">
              {filteredAppointments.length} appointments
            </div>
          </div>
        </div>

        {/* Appointments List */}
        {filteredAppointments.length > 0 ? (
          <div className="space-y-4">
            {filteredAppointments.map((appointment, index) => {
              const datetime = formatDateTime(appointment.scheduledDate);
              const upcoming = isUpcoming(appointment.scheduledDate);

              return (
                <motion.div
                  key={appointment._id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
                >
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className="text-lg font-semibold text-gray-900">
                            {appointment.title}
                          </h3>
                          <span
                            className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-${getStatusColor(
                              appointment.status
                            )}-100 text-${getStatusColor(
                              appointment.status
                            )}-800`}
                          >
                            {getStatusIcon(appointment.status)}
                            <span className="ml-1 capitalize">
                              {appointment.status}
                            </span>
                          </span>
                        </div>

                        {appointment.description && (
                          <p className="text-gray-600 mb-3">
                            {appointment.description}
                          </p>
                        )}

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                          <div className="flex items-center text-gray-600">
                            <FiCalendar className="w-4 h-4 mr-2 text-gray-400" />
                            <span>{datetime.date}</span>
                          </div>
                          <div className="flex items-center text-gray-600">
                            <FiClock className="w-4 h-4 mr-2 text-gray-400" />
                            <span>
                              {datetime.time} ({appointment.duration} min)
                            </span>
                          </div>
                          <div className="flex items-center text-gray-600">
                            {getTypeIcon(appointment.type)}
                            <span className="ml-2 capitalize">
                              {appointment.type}
                            </span>
                          </div>
                          <div className="flex items-center text-gray-600">
                            <FiUser className="w-4 h-4 mr-2 text-gray-400" />
                            <span>Admin</span>
                          </div>
                        </div>

                        {/* Admin Response */}

                        {appointment.adminResponse && (
                          <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                            <div className="flex items-center space-x-2 mb-1">
                              <span className="text-sm font-medium text-blue-900">
                                Admin Response:
                              </span>
                              <span
                                className={`text-xs px-2 py-1 rounded-full capitalize ${
                                  appointment.adminResponse?.status ===
                                  "approved"
                                    ? "bg-green-100 text-green-800"
                                    : appointment.adminResponse?.status ===
                                      "declined"
                                    ? "bg-red-100 text-red-800"
                                    : appointment.adminResponse?.status ===
                                      "reschedule_requested"
                                    ? "bg-orange-100 text-orange-800"
                                    : "bg-gray-100 text-gray-800"
                                }`}
                              >
                                {/* FIX: Add null check before calling replace */}
                                {appointment.adminResponse?.status
                                  ? appointment.adminResponse.status.replace(
                                      "_",
                                      " "
                                    )
                                  : "No Response"}
                              </span>
                            </div>
                            {appointment.adminResponse?.notes && (
                              <p className="text-sm text-blue-700">
                                {appointment.adminResponse.notes}
                              </p>
                            )}
                            {appointment.adminResponse?.proposedDate && (
                              <p className="text-sm text-blue-700 mt-1">
                                Proposed new date:{" "}
                                {
                                  formatDateTime(
                                    appointment.adminResponse.proposedDate
                                  ).full
                                }
                              </p>
                            )}
                          </div>
                        )}

                        {/* Appointment-specific details */}
                        {appointment.type === "virtual" &&
                          appointment.meetingLink && (
                            <div className="mt-3">
                              <a
                                href={appointment.meetingLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                              >
                                Join Virtual Meeting →
                              </a>
                            </div>
                          )}

                        {appointment.type === "onsite" &&
                          appointment.location && (
                            <div className="mt-3 flex items-center text-gray-600 text-sm">
                              <FiMapPin className="w-4 h-4 mr-2" />
                              <span>{appointment.location}</span>
                            </div>
                          )}
                      </div>

                      <div className="flex space-x-2 ml-4">
                        {canCancel(appointment) && (
                          <button
                            onClick={() =>
                              handleCancelAppointment(appointment._id)
                            }
                            className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                            title="Cancel Appointment"
                          >
                            <FiTrash2 size={16} />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Client Notes */}
                    {appointment.clientNotes && (
                      <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="text-sm font-medium text-gray-900 mb-1">
                          Your Notes
                        </div>
                        <div className="text-sm text-gray-600">
                          {appointment.clientNotes}
                        </div>
                      </div>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-12 bg-white rounded-lg shadow-sm border border-gray-200"
          >
            <div className="text-gray-400 text-6xl mb-4">📅</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No appointments found
            </h3>
            <p className="text-gray-500 mb-6">
              {statusFilter !== "all"
                ? "Try adjusting your status filter"
                : "You don't have any appointments scheduled yet."}
            </p>
            <button
              onClick={() => setShowBookingModal(true)}
              className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition-colors"
            >
              Request Your First Appointment
            </button>
          </motion.div>
        )}

        {/* Booking Modal */}
        {showBookingModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold">
                  Request New Appointment
                </h2>
                <button
                  onClick={() => setShowBookingModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>

              <div className="p-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Appointment Title *
                    </label>
                    <input
                      type="text"
                      value={bookingData.title}
                      onChange={(e) =>
                        handleBookingChange("title", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="e.g., Soil Analysis Discussion"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description
                    </label>
                    <textarea
                      value={bookingData.description}
                      onChange={(e) =>
                        handleBookingChange("description", e.target.value)
                      }
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="Brief description of what you'd like to discuss..."
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Appointment Type
                      </label>
                      <select
                        value={bookingData.type}
                        onChange={(e) =>
                          handleBookingChange("type", e.target.value)
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      >
                        {appointmentTypes.map((type) => {
                          const Icon = type.icon;
                          return (
                            <option key={type.value} value={type.value}>
                              {type.label}
                            </option>
                          );
                        })}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Duration (minutes)
                      </label>
                      <select
                        value={bookingData.duration}
                        onChange={(e) =>
                          handleBookingChange(
                            "duration",
                            parseInt(e.target.value)
                          )
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      >
                        <option value={30}>30 minutes</option>
                        <option value={60}>60 minutes</option>
                        <option value={90}>90 minutes</option>
                        <option value={120}>120 minutes</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Preferred Date and Time *
                    </label>
                    <input
                      type="datetime-local"
                      value={bookingData.scheduledDate}
                      onChange={(e) =>
                        handleBookingChange("scheduledDate", e.target.value)
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      required
                      min={new Date().toISOString().slice(0, 16)}
                    />
                  </div>

                  {bookingData.type === "onsite" && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Location/Address
                      </label>
                      <input
                        type="text"
                        value={bookingData.location}
                        onChange={(e) =>
                          handleBookingChange("location", e.target.value)
                        }
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        placeholder="Farm location or address"
                      />
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Additional Notes
                    </label>
                    <textarea
                      value={bookingData.clientNotes}
                      onChange={(e) =>
                        handleBookingChange("clientNotes", e.target.value)
                      }
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="Any specific questions or concerns you'd like to address..."
                    />
                  </div>
                </div>

                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    onClick={() => setShowBookingModal(false)}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleBookAppointment}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    Request Appointment
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ClientAppointments;
